import type {
  LauncherIconKey,
  PublicWebChatAppearance,
  PublicWebChatConfig,
} from './types';

const PANEL_SIZE = {
  compact: { width: 320, height: 480 },
  standard: { width: 380, height: 560 },
  large: { width: 420, height: 620 },
} as const;

const LAUNCHER_SIZE = {
  small: 48,
  medium: 56,
  large: 64,
} as const;

export function resolveAppearance(
  config: PublicWebChatConfig | null | undefined,
): PublicWebChatAppearance {
  const a = config?.appearance ?? {};
  return {
    textColor: a.textColor || '#0f172a',
    launcherIconKey: (a.launcherIconKey as LauncherIconKey) || 'message-bubble',
    launcherIconUrl: a.launcherIconUrl || config?.launcherIcon || null,
    popupMessage: a.popupMessage ?? '',
    widgetSize: a.widgetSize || 'standard',
    displayMode: a.displayMode || 'both',
    offsetX: typeof a.offsetX === 'number' ? a.offsetX : 20,
    offsetY: typeof a.offsetY === 'number' ? a.offsetY : 20,
    companyName: a.companyName ?? '',
    subtitle: a.subtitle ?? '',
    headerBackground: a.headerBackground || config?.primaryColor || '#448AFF',
    headerTextColor: a.headerTextColor || '#ffffff',
    chatBackground: a.chatBackground || '#ffffff',
    borderRadius: typeof a.borderRadius === 'number' ? a.borderRadius : 16,
    messageBubbleRadius:
      typeof a.messageBubbleRadius === 'number' ? a.messageBubbleRadius : 12,
    shadow: a.shadow !== false,
    launcherAnimation: a.launcherAnimation || 'none',
    launcherSize: a.launcherSize || 'medium',
    fontFamily:
      a.fontFamily || "'Segoe UI', system-ui, -apple-system, sans-serif",
    fontSize: typeof a.fontSize === 'number' ? a.fontSize : 14,
    fontWeight: typeof a.fontWeight === 'number' ? a.fontWeight : 400,
    buttonPrimaryColor:
      a.buttonPrimaryColor || config?.primaryColor || '#448AFF',
    buttonHoverColor: a.buttonHoverColor || config?.secondaryColor || '#2979FF',
    buttonRadius: typeof a.buttonRadius === 'number' ? a.buttonRadius : 10,
    showBranding: a.showBranding !== false,
  };
}

export function panelDimensions(size: PublicWebChatAppearance['widgetSize']) {
  return PANEL_SIZE[size] ?? PANEL_SIZE.standard;
}

export function launcherPixelSize(
  size: PublicWebChatAppearance['launcherSize'],
) {
  return LAUNCHER_SIZE[size] ?? LAUNCHER_SIZE.medium;
}

export function isHiddenByDisplayMode(
  displayMode: PublicWebChatAppearance['displayMode'],
): boolean {
  if (typeof window === 'undefined') return false;
  const isMobile = window.matchMedia('(max-width: 768px)').matches;
  if (displayMode === 'desktop' && isMobile) return true;
  if (displayMode === 'mobile' && !isMobile) return true;
  return false;
}

const POPUP_STORAGE_PREFIX = 'rw_popup_seen:';

export function popupStorageKey(publicKey: string): string {
  return `${POPUP_STORAGE_PREFIX}${publicKey}`;
}

export function hasSeenPopupToday(publicKey: string): boolean {
  try {
    const raw = localStorage.getItem(popupStorageKey(publicKey));
    if (!raw) return false;
    const day = new Date().toISOString().slice(0, 10);
    return raw === day;
  } catch {
    return false;
  }
}

export function markPopupSeenToday(publicKey: string): void {
  try {
    const day = new Date().toISOString().slice(0, 10);
    localStorage.setItem(popupStorageKey(publicKey), day);
  } catch {
    // ignore quota / private mode
  }
}

export function launcherIconSvg(key: LauncherIconKey): string {
  switch (key) {
    case 'chat-bubble':
      return `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" x2="9.01" y1="9" y2="9"/><line x1="15" x2="15.01" y1="9" y2="9"/></svg>`;
    case 'message-square':
      return `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>`;
    case 'conversation':
      return `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round"><path d="M14 9a2 2 0 0 1-2 2H6l-4 4V4c0-1.1.9-2 2-2h8a2 2 0 0 1 2 2z"/><path d="M18 9h2a2 2 0 0 1 2 2v11l-4-4h-6a2 2 0 0 1-2-2v-1"/></svg>`;
    case 'message-bubble':
    default:
      return `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round"><path d="M7.9 20A9 9 0 1 0 4 16.1L2 22z"/></svg>`;
  }
}
